import type {
  AuthError,
  CredentialsManagerErrorCodes,
  WebAuthErrorCodes,
} from "react-native-auth0";
import { MfaErrorCodes } from "react-native-auth0";

import { FailedLoginReason } from "@/constants/analytics/Login";
import { LoginErrors } from "@/constants/Login";

type CredentialsManagerErrorType = keyof typeof CredentialsManagerErrorCodes;
type MfaErrorType = keyof typeof MfaErrorCodes;
type WebAuthErrorType = keyof typeof WebAuthErrorCodes;

type AuthErrorType =
  | CredentialsManagerErrorType
  | MfaErrorType
  | WebAuthErrorType;

enum Severity {
  Exception = "exception",
  Message = "message",
  Silent = "silent",
}

// uses UPPER_SNAKE_CASE keys from react-native-auth0
const Auth0CredentialsManagerErrorSeverity: Record<
  CredentialsManagerErrorType,
  Severity
  // eslint-disable-next-line no-restricted-syntax -- over 15 CredentialsManagerErrors
> = {
  API_ERROR: Severity.Exception,
  API_EXCHANGE_FAILED: Severity.Exception,
  BIOMETRICS_FAILED: Severity.Exception,
  CREDENTIAL_MANAGER_ERROR: Severity.Exception,
  // Cryptographic operation failed
  CRYPTO_EXCEPTION: Severity.Exception,
  DPOP_KEY_MISMATCH: Severity.Exception,
  DPOP_KEY_MISSING: Severity.Exception,
  DPOP_NOT_CONFIGURED: Severity.Exception,
  INCOMPATIBLE_DEVICE: Severity.Exception,
  INVALID_CREDENTIALS: Severity.Exception,
  LARGE_MIN_TTL: Severity.Exception,
  // No credentials are stored - user needs to log in
  NO_CREDENTIALS: Severity.Silent,
  NO_NETWORK: Severity.Exception,
  // Refresh token is not available - ensure offline_access scope was requested
  NO_REFRESH_TOKEN: Severity.Message,
  // Failed to refresh credentials using refresh token
  RENEW_FAILED: Severity.Message,
  REVOKE_FAILED: Severity.Exception,
  SESSION_EXPIRED: Severity.Exception,
  STORE_FAILED: Severity.Exception,
  // Unknown or uncategorized error
  UNKNOWN_ERROR: Severity.Exception,
};

// uses UPPER_SNAKE_CASE keys from react-native-auth0
// eslint-disable-next-line no-restricted-syntax -- over 15 MfaErrors
const Auth0MfaErrorSeverity: Record<MfaErrorType, Severity> = {
  ASSOCIATION_REQUIRED: Severity.Exception,
  AUTHENTICATOR_NOT_FOUND: Severity.Exception,
  CHALLENGE_FAILED: Severity.Exception,
  ENROLLMENT_FAILED: Severity.Exception,
  // MFA token has expired - restart MFA flow
  EXPIRED_MFA_TOKEN: Severity.Silent,
  INVALID_BINDING_CODE: Severity.Exception,
  INVALID_EMAIL: Severity.Exception,
  INVALID_MFA_TOKEN: Severity.Exception,
  INVALID_OOB_CODE: Severity.Exception,
  INVALID_OTP: Severity.Exception,
  INVALID_PHONE_NUMBER: Severity.Exception,
  INVALID_RECOVERY_CODE: Severity.Exception,
  // Generic MFA error
  MFA_ERROR: Severity.Silent,
  // Too many verification attempts - rate limited
  TOO_MANY_ATTEMPTS: Severity.Silent,
  UNKNOWN_MFA_ERROR: Severity.Exception,
  UNSUPPORTED_FACTOR: Severity.Exception,
};

// uses UPPER_SNAKE_CASE keys from react-native-auth0
const Auth0WebAuthErrorSeverity: Record<WebAuthErrorType, Severity.Exception> =
  // eslint-disable-next-line no-restricted-syntax -- over 15 WebAuthErrors
  {
    ACCESS_DENIED: Severity.Exception,
    BIOMETRICS_CONFIGURATION_ERROR: Severity.Exception,
    BROWSER_NOT_AVAILABLE: Severity.Exception,
    BROWSER_TERMINATED: Severity.Exception,
    CONSENT_REQUIRED: Severity.Exception,
    FAILED_TO_LOAD_URL: Severity.Exception,
    ID_TOKEN_VALIDATION_FAILED: Severity.Exception,
    INVALID_CALLBACK_URL: Severity.Exception,
    INVALID_CONFIGURATION: Severity.Exception,
    INVALID_INVITATION_URL: Severity.Exception,
    INVALID_STATE: Severity.Exception,
    NETWORK_ERROR: Severity.Exception,
    NO_AUTHORIZATION_CODE: Severity.Exception,
    NO_BUNDLE_IDENTIFIER: Severity.Exception,
    PKCE_NOT_ALLOWED: Severity.Exception,
    TIMEOUT_ERROR: Severity.Exception,
    TRANSACTION_ACTIVE_ALREADY: Severity.Exception,
    // Unknown or uncategorized error
    UNKNOWN_ERROR: Severity.Exception,
    USER_CANCELLED: Severity.Exception,
  };

const AuthErrorSeverityByType: Record<AuthErrorType, Severity> = {
  ...Auth0CredentialsManagerErrorSeverity,
  ...Auth0MfaErrorSeverity,
  ...Auth0WebAuthErrorSeverity,
};

enum AuthErrorCode {
  ExpiredToken = "expired_token",
  InvalidGrant = "invalid_grant",
  InvalidState = "invalid_state",
  MfaRequired = "mfa_required",
  MissingTransaction = "missing_transaction",
  NetworkError = "network_error",
  Timeout = "timeout",
  TooManyAttempts = "too_many_attempts",
  Unauthorized = "unauthorized",
}

const AuthErrorSeverityByCode: Record<AuthErrorCode, Severity | undefined> = {
  [AuthErrorCode.ExpiredToken]: Severity.Silent,
  [AuthErrorCode.InvalidGrant]: Severity.Message,
  [AuthErrorCode.InvalidState]: Severity.Message,
  [AuthErrorCode.MfaRequired]: Severity.Message,
  [AuthErrorCode.MissingTransaction]: Severity.Message,
  [AuthErrorCode.NetworkError]: Severity.Silent,
  [AuthErrorCode.Timeout]: Severity.Silent,
  [AuthErrorCode.TooManyAttempts]: Severity.Message,
  [AuthErrorCode.Unauthorized]: Severity.Message,
};

const AuthErrorCodeToFailedLoginReason: Record<
  string,
  FailedLoginReason | undefined
> = {
  [AuthErrorCode.InvalidGrant]: FailedLoginReason.Credentials,
  [AuthErrorCode.NetworkError]: FailedLoginReason.NetworkError,
  [AuthErrorCode.Timeout]: FailedLoginReason.Auth0Timeout,
  [AuthErrorCode.TooManyAttempts]: FailedLoginReason.TooManyAttempts,
  [AuthErrorCode.Unauthorized]: FailedLoginReason.Unauthorized,
};

const AuthErrorTypeToMessage: { [key: string]: string } = {
  [MfaErrorCodes.INVALID_OTP]: LoginErrors.InvalidMFA,
};

const AuthErrorCodeToMessage: Record<string, string | undefined> = {
  invalid_grant: LoginErrors.InvalidCredentials,
  network_error: LoginErrors.NetworkIssues,
  too_many_attempts: LoginErrors.UserBlocked,
  unauthorized: LoginErrors.InvalidCredentials,
};

type SerializableAuthError = {
  type?: AuthErrorType;
} & Pick<AuthError, "code" | "message" | "name" | "status">;

export type {
  AuthErrorType,
  CredentialsManagerErrorType,
  SerializableAuthError,
};
export {
  AuthErrorCode,
  AuthErrorCodeToFailedLoginReason,
  AuthErrorCodeToMessage,
  AuthErrorSeverityByCode,
  AuthErrorSeverityByType,
  AuthErrorTypeToMessage,
  Severity,
};
